import React, { useState, useEffect, useCallback } from 'react';
import { _registerDialogHandler } from '../utils/dialog';

/**
 * Single in-app dialog, mounted once at the app root. Renders confirm/alert
 * requests from utils/dialog.js as a real modal (so automation and the Chrome
 * extension can click the buttons — native window.confirm can't be).
 */
export default function GlobalDialog() {
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    _registerDialogHandler(setDialog);
    return () => _registerDialogHandler(null);
  }, []);

  const close = useCallback((result) => {
    setDialog((d) => { d?.resolve(result); return null; });
  }, []);

  // Esc cancels, Enter confirms.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close(dialog.kind === 'confirm' ? false : undefined);
      else if (e.key === 'Enter') close(dialog.kind === 'confirm' ? true : undefined);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, close]);

  if (!dialog) return null;

  const isConfirm = dialog.kind === 'confirm';

  return (
    <div className="appdialog-overlay" onClick={() => close(isConfirm ? false : undefined)}>
      <div className="appdialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {dialog.title && <div className="appdialog-title">{dialog.title}</div>}
        <div className="appdialog-message">{dialog.message}</div>
        <div className="appdialog-actions">
          {isConfirm && (
            <button
              type="button"
              className="appdialog-btn appdialog-cancel"
              data-testid="dialog-cancel"
              onClick={() => close(false)}
            >
              {dialog.cancelText || 'Cancel'}
            </button>
          )}
          <button
            type="button"
            className={`appdialog-btn appdialog-confirm${dialog.danger ? ' appdialog-danger' : ''}`}
            data-testid="dialog-confirm"
            autoFocus
            onClick={() => close(isConfirm ? true : undefined)}
          >
            {dialog.confirmText || (isConfirm ? 'Confirm' : 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
}
